import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { CockpitSide, CockpitSpendSide, CockpitSpendView } from '@vello/api-client';
import { formatMarketInstant, paceMessageKey, paceOf, paceTone, spendSeries, type Pace } from '../../../src/index';
import { useAdmin } from '../../session';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { AUTO_REFRESH_MS, EmptyState, KpiGrid, LineChart, Notice, Panel, SegmentedControl, Stack, StatCard, Text, useAutoRefresh } from '@vello/web-theme';
import { minorMoney, wireMoney } from './shared';

/**
 * The cockpit's money panel (W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 sections 3.1 and 3.4): for each envelope the controller draws on (riders, drivers, couriers) the balance still free,
 * what was committed today and where that stands against the controller's pacing plan, and the day's cumulative spend curve against the plan, hour by hour in the market's time zone. A market
 * with no controller policy has no plan: the spend is still shown and the panel says so. A refused read is an ERROR, never an empty chart. The data refreshes itself.
 */
const SIDES: readonly CockpitSide[] = ['RIDER', 'DRIVER', 'COURIER'];

export function SpendPanel({ market }: { readonly market: string }): ReactElement {
  const { runtime } = useAdmin();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const [view, setView] = useState<CockpitSpendView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [side, setSide] = useState<CockpitSide>('RIDER');
  const seq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    if (market === '') {
      setView(undefined);
      return;
    }
    const res = await runtime.incentiveCockpit.spend(market);
    if (mine !== seq.current) return;
    if (res.ok) {
      setView(res.value);
      setError(undefined);
    } else {
      // A refused read keeps the figures unknown: the panel says it could not load, never "nothing spent".
      setView(undefined);
      setError(t('cpt.spend.error', { code: fail(res.error) }));
    }
  }, [runtime, market, t, fail]);

  useEffect(() => {
    setView(undefined);
    setError(undefined);
    void load();
    // Reload when the header scope names another market.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  // The zone the SERVER cut the day in (never the console's own list of markets): the hours, the plan and the captions all speak it.
  const tz = view?.timeZone ?? undefined;
  const paceText = (p: Pace): string =>
    p.state === 'NO_PLAN' ? t('cpt.pace.NO_PLAN') : p.state === 'NOT_STARTED' ? t('cpt.pace.NOT_STARTED') : t(paceMessageKey(p.state, view?.controllerMode), { pct: Math.round((p.ratioBp ?? 0) / 100) });

  if (error !== undefined) return <Notice tone="danger">{error}</Notice>;
  if (view === undefined) return <Panel><EmptyState title={t('ui.loading')} /></Panel>;
  if (view.state === 'NO_FUND' || view.currency === null) return <Panel><EmptyState title={t('cpt.spend.noFund')} /></Panel>;

  const currency = view.currency;
  const rows = SIDES.map((s) => view.sides.find((x) => x.side === s)).filter((x): x is CockpitSpendSide => x !== undefined);
  const chosen = rows.find((r) => r.side === side) ?? rows[0];
  const series = chosen !== undefined ? spendSeries(chosen) : undefined;

  return (
    <Stack gap={4}>
      <Text role="caption" tone="tertiary">
        <span className="tnum">{t('cpt.spend.asOf', { when: formatMarketInstant(view.generatedAt, tz, lang), spent: wireMoney(view.daySpent, lang) })}</span>
      </Text>
      {view.state === 'NO_PLAN' && <Notice tone="warning">{t('cpt.spend.noPlan')}</Notice>}
      <KpiGrid>
        {rows.map((r) => {
          const pace = paceOf(r, view.minuteOfDay);
          return (
            <StatCard
              key={r.side}
              label={v('incentive_fund.side', r.side)}
              value={wireMoney(r.committedToday, lang)}
              tone={paceTone(pace.state) === 'warning' ? 'warning' : paceTone(pace.state) === 'success' ? 'success' : undefined}
              hint={t('cpt.spend.sideHint', { available: wireMoney(r.available, lang), budget: wireMoney(r.dayBudget, lang), pace: paceText(pace) })}
            />
          );
        })}
      </KpiGrid>
      <Panel>
        <Stack gap={3}>
          <h3>{t('cpt.spend.chartTitle')}</h3>
          <SegmentedControl<CockpitSide> ariaLabel={t('cpt.spend.side')} value={chosen?.side ?? side} onChange={setSide} options={rows.map((r) => ({ id: r.side, label: v('incentive_fund.side', r.side) }))} />
          {series !== undefined && (
            <LineChart
              title={t('cpt.spend.chartTitle')}
              labels={series.labels.map((h) => t('cpt.spend.hourEnd', { hour: String(h + 1).padStart(2, '0') }))}
              series={[
                { key: 'committed', label: t('cpt.chart.committed'), values: series.committed },
                ...(series.planned.some((p) => p !== null) ? [{ key: 'planned', label: t('cpt.chart.planned'), values: series.planned }] : []),
              ]}
              formatValue={(n) => minorMoney(n, currency, lang)}
              emptyLabel={t('cpt.spend.chartEmpty')}
              height={240}
            />
          )}
          <Text role="caption" tone="tertiary">{t('cpt.spend.chartNote', { zone: tz ?? 'UTC' })}</Text>
        </Stack>
      </Panel>
    </Stack>
  );
}
